import { c as _c } from "react/compiler-runtime";
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useSearchParams } from 'next/navigation';
import { parseAsString, useQueryStates } from 'nuqs';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useCookie } from 'react-use';
import { activeCheckoutCouponCode, checkoutCouponQueryForApply, checkoutCouponQueryForRemove, resolveCheckoutCouponCode } from './checkoutParams';
const COUPON_QUERY_OPTIONS = {
  history: 'replace' as const,
  scroll: false,
  shallow: true
};
let promoCookieIgnored = false;
const promoCookieListeners = new Set<() => void>();
function subscribePromoCookieIgnored(listener: () => void) {
  promoCookieListeners.add(listener);
  return () => {
    promoCookieListeners.delete(listener);
    if (promoCookieListeners.size === 0) {
      promoCookieIgnored = false;
    }
  };
}
function getPromoCookieIgnored() {
  return promoCookieIgnored;
}
function ignorePromoCookie() {
  if (promoCookieIgnored) {
    return;
  }
  promoCookieIgnored = true;
  promoCookieListeners.forEach(listener => listener());
}

/**
 * Coupon code for a promotion-code field. Apply and remove track `?couponCode=` the way
 * `CouponForm` does, so a reload or a shared link keeps the code the member entered.
 */
export function useCheckoutCouponCode() {
  const $ = _c(25);
  const params = useSearchParams();
  const {
    currentUser
  } = useCurrentUser();
  const [promoCookie,, deletePromoCookie] = useCookie("sco_promo");
  const promoCookieIgnoredNow = useSyncExternalStore(subscribePromoCookieIgnored, getPromoCookieIgnored, _temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      couponCode: parseAsString,
      promo: parseAsString,
      fgCouponCode: parseAsString,
      facCouponCode: parseAsString
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [, setCouponParams] = useQueryStates(t0, COUPON_QUERY_OPTIONS);
  const [pending, setPending] = useState(undefined);
  const [fieldCode, setFieldCode] = useState(null);
  const pendingBaseline = useRef(null);
  let t1;
  if ($[1] !== params) {
    t1 = params?.get("couponCode")?.trim() || null;
    $[1] = params;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const urlCoupon = t1;
  let t2;
  if ($[3] !== currentUser?.defaultCouponCode || $[4] !== params || $[5] !== pending || $[6] !== promoCookie || $[7] !== promoCookieIgnoredNow) {
    const resolved = resolveCheckoutCouponCode({
      params,
      defaultCouponCode: currentUser?.defaultCouponCode,
      promoCookie: promoCookieIgnoredNow ? null : promoCookie
    });
    t2 = activeCheckoutCouponCode(pending, resolved);
    $[3] = currentUser?.defaultCouponCode;
    $[4] = params;
    $[5] = pending;
    $[6] = promoCookie;
    $[7] = promoCookieIgnoredNow;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const couponCode = t2;
  let t3;
  let t4;
  if ($[9] !== pending || $[10] !== urlCoupon) {
    t3 = () => {
      if (pending === undefined) {
        return;
      }
      if (pending === null) {
        if (urlCoupon && urlCoupon !== pendingBaseline.current) {
          setPending(undefined);
        }
        return;
      }
      if (urlCoupon !== pendingBaseline.current) {
        setPending(undefined);
      }
    };
    t4 = [pending, urlCoupon];
    $[9] = pending;
    $[10] = urlCoupon;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== setCouponParams || $[14] !== urlCoupon) {
    t5 = code => {
      const patch = checkoutCouponQueryForApply(code);
      if (!patch) {
        return;
      }
      pendingBaseline.current = urlCoupon;
      setPending(patch.couponCode);
      setFieldCode(patch.couponCode);
      setCouponParams(patch);
    };
    $[13] = setCouponParams;
    $[14] = urlCoupon;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const applyCouponCode = t5;
  let t6;
  if ($[16] !== deletePromoCookie || $[17] !== setCouponParams || $[18] !== urlCoupon) {
    t6 = () => {
      pendingBaseline.current = urlCoupon;
      setPending(null);
      setFieldCode(null);
      ignorePromoCookie();
      deletePromoCookie();
      setCouponParams(checkoutCouponQueryForRemove());
    };
    $[16] = deletePromoCookie;
    $[17] = setCouponParams;
    $[18] = urlCoupon;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const removeCouponCode = t6;
  const t7 = Boolean(fieldCode && fieldCode === couponCode);
  let t8;
  if ($[20] !== applyCouponCode || $[21] !== couponCode || $[22] !== removeCouponCode || $[23] !== t7) {
    t8 = {
      applyCouponCode,
      couponCode,
      couponFieldActive: t7,
      removeCouponCode
    };
    $[20] = applyCouponCode;
    $[21] = couponCode;
    $[22] = removeCouponCode;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
function _temp() {
  return false;
}